import React from "react";
import { Alert, Button, Card, CardHeader, Metric, ProgressBar, Table } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import { formatPrice } from "../../../../shared/teamCurrencyFormatting";
import { formatCount, formatPercent, prettyUrl, ratio } from "./reportMath";

const styles = {
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 16,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 6,
    padding: "10px 0",
    fontSize: 14,
  },
  muted: {
    fontSize: 13,
    color: "var(--sl-text-tertiary)",
  },
  dot: (color) => ({
    display: "inline-block",
    width: 8,
    height: 8,
    marginRight: 8,
    borderRadius: "50%",
    background: color,
  }),
};

const emailSections = ({ overviewMetrics: metrics, totalClicks, conversionSummary, salesFooter }) => {
  const delivered = metrics.delivered_count;
  const opened = metrics.opened_count;
  const clicked = metrics.clicked_count;

  return {
    metrics: [
      { label: "Delivered", value: formatPercent(delivered, metrics.sent_count), footer: `${formatCount(delivered)} of ${formatCount(metrics.sent_count)} sent` },
      { label: "Opened", value: formatPercent(opened, delivered), footer: `${formatCount(opened)} fans opened` },
      { label: "Link clicks", value: formatCount(totalClicks), footer: `${formatCount(clicked)} fans · ${formatPercent(clicked, delivered)} of delivered` },
      { label: "Tickets sold", value: formatCount(conversionSummary.tickets_sold), footer: salesFooter },
    ],
    delivered,
    engagement: [
      { label: "Opened", detail: `${formatCount(opened)} fans`, value: ratio(opened, delivered) },
      { label: "Clicked", detail: `${formatCount(clicked)} fans`, value: ratio(clicked, delivered) },
      { label: "Click to open", detail: `${formatCount(clicked)} of ${formatCount(opened)} opened`, value: ratio(clicked, opened) },
      { label: "Converted", detail: `${formatCount(conversionSummary.orders)} orders | ${formatCount(conversionSummary.tickets_sold)} tickets`, value: ratio(conversionSummary.orders, delivered) },
    ],
    engagementNote: "Many email apps block open tracking, so opens read low. Link clicks are the more reliable signal.",
    deliverySummary: `${formatCount(metrics.sent_count)} emails sent`,
    delivery: [
      { label: "Delivered", count: delivered, base: metrics.sent_count, color: "var(--sl-fg-success)" },
      { label: "Bounced", count: metrics.bounced_count, base: delivered, color: "var(--sl-fg-error)" },
      { label: "Suppressed", count: metrics.suppressed_count, base: delivered, color: "var(--sl-fg-warning)" },
      { label: "Failed", count: metrics.failed_count, base: null, color: "var(--sl-fg-quaternary)" },
      { label: "Unsubscribed", count: metrics.unsubscribed_count, base: delivered, color: "var(--sl-fg-quaternary)" },
    ],
    contentButton: "View heatmap",
    showLinks: true,
  };
};

const smsSections = ({ overviewMetrics: metrics, links, totalClicks, conversionSummary, salesFooter }) => {
  const statuses = metrics.status_counts;
  const sent = metrics.sent_count;
  const delivered = metrics.delivered_count;
  const showLinks = links.length > 0;
  const clicksMetric = showLinks
    ? { label: "Link clicks", value: formatCount(totalClicks), footer: `${formatCount(statuses.clicked)} fans · ${formatPercent(statuses.clicked, delivered)} of delivered` }
    : { label: "Fans clicked", value: formatCount(statuses.clicked), footer: `${formatPercent(statuses.clicked, delivered)} of delivered` };

  return {
    metrics: [
      { label: "Delivered", value: formatPercent(delivered, sent), footer: `${formatCount(delivered)} of ${formatCount(sent)} sent` },
      clicksMetric,
      { label: "Tickets sold", value: formatCount(conversionSummary.tickets_sold), footer: salesFooter },
    ],
    delivered,
    engagement: [
      { label: "Clicked", detail: `${formatCount(statuses.clicked)} fans`, value: ratio(statuses.clicked, delivered) },
      { label: "Converted", detail: `${formatCount(conversionSummary.orders)} orders | ${formatCount(conversionSummary.tickets_sold)} tickets`, value: ratio(conversionSummary.orders, delivered) },
    ],
    engagementNote: "Texts don't report opens. Link clicks and ticket sales are how you measure an SMS.",
    deliverySummary: `${formatCount(sent)} texts sent`,
    delivery: [
      { label: "Delivered", count: delivered, base: sent, color: "var(--sl-fg-success)" },
      { label: "Failed", count: statuses.failed, base: sent, color: "var(--sl-fg-error)" },
      { label: "Already opted out", count: statuses.opted_out, base: sent, color: "var(--sl-fg-warning)" },
      { label: "Awaiting carrier", count: statuses.pending, base: sent, color: "var(--sl-fg-quaternary)" },
    ],
    contentButton: "View message",
    showLinks,
  };
};

const ReportOverview = ({ conversionSummary, onViewHeatmap }) => {
  const { team, overviewMetrics, overviewLinkClicks, isEmailTemplate } = useBroadcastEditContext();

  const links = overviewLinkClicks || [];
  const totalClicks = links.reduce((sum, link) => sum + link.clicks, 0);
  const salesFooter = `${formatPrice(conversionSummary.revenue, team.base_currency_symbol)} in sales within 7 days`;
  const sections = (isEmailTemplate ? emailSections : smsSections)({ overviewMetrics, links, totalClicks, conversionSummary, salesFooter });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 50 }}>
      <div style={styles.metrics}>
        {sections.metrics.map((metric) => (
          <Metric key={metric.label} label={metric.label} value={metric.value} footer={<span style={styles.muted}>{metric.footer}</span>} />
        ))}
      </div>

      <div style={styles.columns}>
        <Card header={<CardHeader title="Engagement" supportingText={`Out of ${formatCount(sections.delivered)} delivered`} />}>
          {sections.engagement.map((item) => (
            <div key={item.label} style={{ padding: "8px 0" }}>
              <div style={{ ...styles.row, padding: 0 }}>
                <span><strong>{item.label}</strong> <span style={styles.muted}>{item.detail}</span></span>
                <span>{item.value.toFixed(1)}%</span>
              </div>
              <ProgressBar value={item.value} label="none" />
            </div>
          ))}
          <div style={{ marginTop: 12 }}>
            <Alert color="gray" title={sections.engagementNote} />
          </div>
        </Card>

        <Card header={<CardHeader title="Delivery" supportingText={sections.deliverySummary} />}>
          {sections.delivery.map((item) => (
            <div key={item.label} style={styles.row}>
              <span><span style={styles.dot(item.color)} />{item.label}</span>
              <span>{formatCount(item.count)} {item.base !== null && <span style={styles.muted}>{formatPercent(item.count, item.base)}</span>}</span>
            </div>
          ))}
        </Card>
      </div>

      {sections.showLinks && (
        <Card
          padding="none"
          header={(
            <CardHeader
              title="Top clicked links"
              supportingText={`${formatCount(totalClicks)} tracked clicks across ${links.length} links`}
              actions={links.length > 0 && <Button variant="secondary" size="sm" onClick={onViewHeatmap}>{sections.contentButton}</Button>}
            />
          )}
        >
          <Table
            columns={[
              { key: "url", header: "Link", primary: true, render: (link) => <a href={link.url} target="_blank" rel="noopener noreferrer">{prettyUrl(link.url)}</a> },
              { key: "clicks", header: "Clicks", align: "right", render: (link) => formatCount(link.clicks) },
              { key: "unique_clicks", header: "Unique", align: "right", render: (link) => formatCount(link.unique_clicks) },
            ]}
            data={links}
            getRowKey={(link) => link.url}
            empty={<div style={styles.muted}>No link clicks tracked yet.</div>}
          />
        </Card>
      )}
    </div>
  );
};

export default ReportOverview;
